import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { 
  Plus,
  TrendingUp, 
  TrendingDown, 
  LineChart,
  DollarSign,
  Trash2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import TradingCharts from '../components/TradingCharts';

interface Trade {
  id: string;
  trade_date: string;
  symbol: string;
  entry_price: number;
  exit_price: number;
  position_size: number;
  trade_type: 'long' | 'short';
  result: number;
  notes: string;
  created_at: string;
  r_multiple?: number;
}

// Lista de símbolos disponibles
const TRADING_SYMBOLS = {
  'Forex': [
    { symbol: 'EUR/USD', name: 'Euro / Dólar', pip_value: 10 },
    { symbol: 'GBP/USD', name: 'Libra / Dólar', pip_value: 10 },
    { symbol: 'USD/JPY', name: 'Dólar / Yen Japonés', pip_value: 1000 },
    { symbol: 'USD/CHF', name: 'Dólar / Franco Suizo', pip_value: 10 },
    { symbol: 'USD/CAD', name: 'Dólar / Dólar Canadiense', pip_value: 10 }
  ],
  'Futuros': [
    { symbol: 'ES', name: 'E-mini S&P 500', point_value: 50 },
    { symbol: 'NQ', name: 'E-mini Nasdaq 100', point_value: 20 },
    { symbol: 'YM', name: 'Dow Jones', point_value: 5 },
    { symbol: 'CL', name: 'Crudo WTI', point_value: 10 },
    { symbol: 'GC', name: 'Oro', point_value: 100 },
    { symbol: 'SI', name: 'Plata', point_value: 50 },
    { symbol: 'ZC', name: 'Maíz', point_value: 50 },
    { symbol: 'ZS', name: 'Soya', point_value: 50 },
    { symbol: 'KC', name: 'Café', point_value: 375 }
  ]
};

const Journal: React.FC = () => {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [isAddingTrade, setIsAddingTrade] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newTrade, setNewTrade] = useState({
    trade_date: new Date().toISOString().split('T')[0],
    symbol: '',
    entry_price: 0,
    exit_price: 0,
    position_size: 0,
    trade_type: 'long' as const,
    notes: ''
  });

  useEffect(() => {
    fetchTrades();
  }, []);

  const fetchTrades = async () => {
    try {
      setLoading(true);
      setError('');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró la sesión del usuario');

      const { data, error } = await supabase
        .from('trading_journal')
        .select('*')
        .eq('user_id', user.id)
        .order('trade_date', { ascending: false });

      if (error) throw error;
      setTrades(data || []);
    } catch (err: any) {
      console.error('Error fetching trades:', err);
      setError(err.message || 'Error al cargar las operaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró la sesión del usuario');

      // Buscar point_value del símbolo (futuros tienen multiplicador)
      const allSymbols = [...TRADING_SYMBOLS['Futuros'], ...TRADING_SYMBOLS['Forex']];
      const symbolInfo = allSymbols.find(s => s.symbol === newTrade.symbol);
      const pointValue = (symbolInfo && 'point_value' in symbolInfo) ? symbolInfo.point_value : 1;

      const priceDiff = newTrade.trade_type === 'long'
        ? newTrade.exit_price - newTrade.entry_price
        : newTrade.entry_price - newTrade.exit_price;
      const result = priceDiff * newTrade.position_size * pointValue;

      const { error } = await supabase
        .from('trading_journal')
        .insert([{
          ...newTrade,
          result,
          user_id: user.id
        }]);

      if (error) throw error;

      setIsAddingTrade(false);
      setNewTrade({
        trade_date: new Date().toISOString().split('T')[0],
        symbol: '',
        entry_price: 0,
        exit_price: 0,
        position_size: 0,
        trade_type: 'long',
        notes: ''
      });
      setSuccess('Operación registrada correctamente');
      await fetchTrades();

      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      console.error('Error adding trade:', err);
      setError(err.message || 'Error al registrar la operación');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setError('');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró la sesión del usuario');

      const { error } = await supabase
        .from('trading_journal')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      setTrades(trades.filter(t => t.id !== id));
      setSuccess('Operación eliminada correctamente');

      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      console.error('Error deleting trade:', err);
      setError(err.message || 'Error al eliminar la operación');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white">Diario de Trading</h1>
            <p className="mt-2 text-gray-400">
              Registra y analiza tus operaciones
            </p>
          </div>
          <button
            onClick={() => setIsAddingTrade(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Nueva Operación</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-900/20 border border-green-900/50 rounded-lg p-4 flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
            <p className="text-green-500">{success}</p>
          </div>
        )}

        {/* Trading Charts */}
        {trades.length > 0 && (
          <TradingCharts trades={trades} />
        )}

        {/* Add Trade Modal */}
        {isAddingTrade && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-dark-card border border-white/10 rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold text-white mb-4">Nueva Operación</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300">Fecha</label>
                  <input
                    type="date"
                    value={newTrade.trade_date}
                    onChange={e => setNewTrade({...newTrade, trade_date: e.target.value})}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300">Símbolo</label>
                  <select
                    value={newTrade.symbol}
                    onChange={e => setNewTrade({...newTrade, symbol: e.target.value})}
                    className="input-field"
                    required
                  >
                    <option value="">Seleccionar símbolo</option>
                    {Object.entries(TRADING_SYMBOLS).map(([category, symbols]) => (
                      <optgroup key={category} label={category}>
                        {symbols.map(s => (
                          <option key={s.symbol} value={s.symbol}>
                            {s.symbol} - {s.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Precio Entrada</label>
                    <input
                      type="number"
                      value={newTrade.entry_price}
                      onChange={e => setNewTrade({...newTrade, entry_price: parseFloat(e.target.value)})}
                      className="input-field"
                      step="any"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Precio Salida</label>
                    <input
                      type="number"
                      value={newTrade.exit_price}
                      onChange={e => setNewTrade({...newTrade, exit_price: parseFloat(e.target.value)})}
                      className="input-field"
                      step="any"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300">Tamaño Posición</label>
                  <input
                    type="number"
                    value={newTrade.position_size}
                    onChange={e => setNewTrade({...newTrade, position_size: parseFloat(e.target.value)})}
                    className="input-field"
                    step="any"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300">Tipo</label>
                  <select
                    value={newTrade.trade_type}
                    onChange={e => setNewTrade({...newTrade, trade_type: e.target.value as 'long' | 'short'})}
                    className="input-field"
                  >
                    <option value="long">Long</option>
                    <option value="short">Short</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300">Notas</label>
                  <textarea
                    value={newTrade.notes}
                    onChange={e => setNewTrade({...newTrade, notes: e.target.value})}
                    className="input-field"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setIsAddingTrade(false)}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary">
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Trades List */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-white">Operaciones Recientes</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-white/10">
                  <th className="pb-3 text-gray-400">Fecha</th>
                  <th className="pb-3 text-gray-400">Símbolo</th>
                  <th className="pb-3 text-gray-400">Tipo</th>
                  <th className="pb-3 text-gray-400">Entrada</th>
                  <th className="pb-3 text-gray-400">Salida</th>
                  <th className="pb-3 text-gray-400">Tamaño</th>
                  <th className="pb-3 text-gray-400">Resultado</th>
                  <th className="pb-3 text-gray-400">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {trades.map(trade => (
                  <tr key={trade.id} className="hover:bg-white/5">
                    <td className="py-3 text-white">
                      {new Date(trade.trade_date).toLocaleDateString()}
                    </td>
                    <td className="py-3 text-white">{trade.symbol}</td>
                    <td className="py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        trade.trade_type === 'long' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                      }`}>
                        {trade.trade_type === 'long' ? (
                          <TrendingUp className="h-3 w-3 mr-1" />
                        ) : (
                          <TrendingDown className="h-3 w-3 mr-1" />
                        )}
                        {trade.trade_type.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 text-white">${trade.entry_price.toFixed(2)}</td>
                    <td className="py-3 text-white">{trade.exit_price != null ? `$${trade.exit_price.toFixed(2)}` : '—'}</td>
                    <td className="py-3 text-white">{trade.position_size}</td>
                    <td className={`py-3 font-medium ${
                      (trade.result || 0) > 0 ? 'text-green-500' : (trade.result || 0) < 0 ? 'text-red-500' : 'text-yellow-400'
                    }`}>
                      {trade.result != null ? `$${trade.result.toFixed(2)}` : 'Abierto'}
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => handleDelete(trade.id)}
                        className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-500 hover:text-red-400"
                        title="Eliminar operación"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {trades.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-400">
                      No hay operaciones registradas
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Journal;